import { create } from 'zustand';

import { brandService, DEFAULT_BRAND, type Brand } from '@/services/brand';

interface BrandState {
  brand: Brand;
  loaded: boolean;
  loadBrand: () => Promise<void>;
  applyBrand: (brand: Brand) => void;
}

/** Inject the brand into the live DOM: CSS vars (color), <title>, favicon. */
function applyBrandToDom(brand: Brand): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Brand color → override --nexus-brand and derive readable variants at
  // runtime (color-mix is evaluated by the browser). Inline style on <html>
  // wins over the :root / [data-theme] blocks in tokens.css, so it applies
  // across claro/escuro/black.
  const c = brand.brand_color?.trim();
  const keys = [
    '--nexus-brand',
    '--nexus-brand-hover',
    '--nexus-brand-active',
    '--nexus-brand-glow',
    '--nexus-brand-bg',
  ];
  if (c) {
    root.style.setProperty('--nexus-brand', c);
    root.style.setProperty('--nexus-brand-hover', `color-mix(in srgb, ${c} 78%, white)`);
    root.style.setProperty('--nexus-brand-active', `color-mix(in srgb, ${c} 85%, black)`);
    root.style.setProperty('--nexus-brand-glow', `color-mix(in srgb, ${c} 35%, transparent)`);
    root.style.setProperty('--nexus-brand-bg', `color-mix(in srgb, ${c} 14%, transparent)`);
  } else {
    // Empty = fall back to tokens.css defaults.
    keys.forEach((k) => root.style.removeProperty(k));
  }

  // Document title.
  document.title = brand.sub ? `${brand.name} ${brand.sub}` : brand.name;

  // Favicon (optional).
  if (brand.favicon?.trim()) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = brand.favicon;
  }
}

export const useBrand = create<BrandState>((set, get) => ({
  brand: DEFAULT_BRAND,
  loaded: false,
  applyBrand: (brand) => {
    applyBrandToDom(brand);
    set({ brand, loaded: true });
  },
  loadBrand: async () => {
    try {
      const brand = await brandService.get();
      get().applyBrand(brand);
    } catch {
      // Network/boot error → keep the neutral default (already applied by CSS).
      set({ loaded: true });
    }
  },
}));
